/**
 * Payment Design Preview — mock ViewModel only.
 * Pay now: hosted card · bank transfer · optional split.
 * Separate option: hold without ticketing (when holdEligible).
 * No provider / API / pricing math in UI.
 */

import { getMockReviewViewModel } from "../review/reviewMockData";

export const PAYMENT_PREVIEW_STATES = [
  { id: "card", label: "A · Credit card (hosted)" },
  { id: "split", label: "B · Split payment" },
  { id: "bank", label: "C · Bank transfer" },
  { id: "hold", label: "D · Hold" },
  { id: "failed", label: "E · Payment failed" },
  { id: "processing", label: "F · Processing" },
  { id: "success", label: "G · Success" },
  { id: "embedded", label: "H · Embedded fields (preview)" },
];

/**
 * Build Payment screen ViewModel for Design Preview.
 * Reuses Review bookingSummary strings for StickyBookingSummary.
 */
export function getMockPaymentViewModel(offer, stateId = "card") {
  const review = getMockReviewViewModel(offer);
  const payableLabel =
    review.bookingSummary?.totals?.payableTotalLabel || "₪1,395";
  const payableAmount = "₪1,395";

  const known = PAYMENT_PREVIEW_STATES.some((s) => s.id === stateId)
    ? stateId
    : "card";

  /** Map preview state → UI selection + status overlay */
  let selectedActionId = "card";
  let status = null;
  let cardMode = "hosted";
  let splitOpen = false;
  let holdEligible = true;

  switch (known) {
    case "bank":
      selectedActionId = "bank";
      break;
    case "hold":
      selectedActionId = "hold";
      break;
    case "split":
      selectedActionId = "card";
      splitOpen = true;
      break;
    case "failed":
      selectedActionId = "card";
      status = "failed";
      break;
    case "processing":
      selectedActionId = "card";
      status = "processing";
      break;
    case "success":
      selectedActionId = "card";
      status = "success";
      break;
    case "embedded":
      selectedActionId = "card";
      cardMode = "embedded";
      break;
    case "card":
    default:
      selectedActionId = "card";
      cardMode = "hosted";
      break;
  }

  return {
    title: "תשלום",
    payable: {
      caption: "לתשלום עכשיו",
      amountLabel: payableAmount,
    },
    methodsTitle: "בחר אמצעי תשלום",
    methods: [
      { id: "card", label: "כרטיס אשראי" },
      { id: "bank", label: "העברה בנקאית" },
    ],
    selectedActionId,
    status,
    cardMode,
    holdEligible,
    hostedCard: {
      title: "כרטיס אשראי",
      amountCaption: "סכום לתשלום",
      amountLabel: payableAmount,
      secureNote: "התשלום יתבצע בעמוד תשלום מאובטח",
      ctaLabel: `לתשלום ${payableAmount}`,
    },
    embeddedCard: {
      title: "פרטי כרטיס",
      previewBadge: "Preview בלבד · Embedded fields · לא למוצר",
      mockNote: "Design Preview · שדות תצוגה בלבד · ללא סליקה",
      fields: [
        {
          id: "cardNumber",
          label: "מספר כרטיס",
          placeholder: "•••• •••• •••• 4242",
          value: "4242 4242 4242 4242",
          ltr: true,
        },
        {
          id: "cardHolder",
          label: "שם בעל הכרטיס",
          placeholder: "NAME ON CARD",
          value: "DAVID COHEN",
          ltr: true,
        },
        {
          id: "expiry",
          label: "תוקף",
          placeholder: "MM/YY",
          value: "12/28",
          ltr: true,
        },
        {
          id: "cvv",
          label: "CVV",
          placeholder: "•••",
          value: "•••",
          ltr: true,
        },
      ],
      ctaLabel: `לתשלום ${payableAmount}`,
    },
    split: {
      initiallyOpen: splitOpen,
      toggleLabel: "פיצול תשלום למספר כרטיסים",
      totalLabel: "סה״כ",
      totalAmountLabel: payableAmount,
      remainingLabel: "יתרה לחלוקה",
      remainingAmountLabel: "₪0",
      addCardLabel: "הוסף כרטיס נוסף",
      progressPaidLabel: "שולם ₪0 מתוך ₪1,395",
      progressRemainingLabel: "נותר ₪1,395",
      cards: [
        {
          id: "split-1",
          label: "כרטיס 1",
          amountLabel: "₪700",
          payLabel: "שלם כרטיס 1",
          paidLabel: "✓ שולם ₪700",
          status: "pending",
        },
        {
          id: "split-2",
          label: "כרטיס 2",
          amountLabel: "₪695",
          payLabel: "שלם כרטיס 2",
          paidLabel: "✓ שולם ₪695",
          status: "pending",
        },
      ],
    },
    bankDetails: {
      title: "העברה בנקאית",
      amountCaption: "סכום להעברה",
      amountLabel: payableAmount,
      accountTitle: "פרטי חשבון",
      lines: [
        { label: "מוטב", value: "HotelsRiver Ltd" },
        { label: "בנק", value: "לאומי" },
        { label: "סניף", value: "800" },
        { label: "חשבון", value: "123456/78" },
        { label: "Reference להזמנה", value: "HR-FLIGHT-MOCK" },
      ],
      nextStepTitle: "המשך התהליך",
      nextStepNote:
        "לאחר ההעברה יתאפשר אישור / העלאת אסמכתא / OTP לפי הכללים במערכת — Preview בלבד.",
      ctaLabel: "המשך להוראות העברה",
    },
    moreOptions: {
      title: "אפשרויות הזמנה נוספות",
      hold: {
        id: "hold",
        label: "שמירת הזמנה ללא כרטוס",
        availableUntilLabel: "זמין עד 18:30 היום",
        priceGuaranteedLabel: "האם המחיר מובטח",
        priceGuaranteedValue: "לא — המחיר אינו מובטח לאחר מועד זה",
        afterExpiryLabel: "מה קורה לאחר פקיעת ה-Hold",
        afterExpiryValue: "השמירה פוקעת ויש לחפש מחדש / להזמין מחדש",
        paymentLaterLabel: "האם נדרש תשלום בהמשך",
        paymentLaterValue: "כן — יש להשלים תשלום לפני הכרטוס",
        ctaLabel: "שמור הזמנה",
      },
    },
    statusCopy: {
      processing: {
        title: "מעבד את התשלום...",
        body: "אנא המתינו. אין לבצע פעולה נוספת כרגע.",
      },
      success: {
        title: "תשלום התקבל",
        body: "התשלום אושר. ניתן להמשיך לאישור ההזמנה.",
      },
      failed: {
        title: "התשלום לא אושר",
        body: "לא התקבל אישור מהספק. ניתן לנסות שוב או לבחור אמצעי תשלום אחר.",
        retryLabel: "נסה שוב",
        otherMethodLabel: "בחר אמצעי תשלום אחר",
      },
    },
    ctaByAction: {
      card: {
        continueLabel: `לתשלום ${payableAmount}`,
        backLabel: "חזרה לסיכום",
      },
      bank: {
        continueLabel: "המשך להוראות העברה",
        backLabel: "חזרה לסיכום",
      },
      hold: {
        continueLabel: "שמור הזמנה",
        backLabel: "חזרה לסיכום",
      },
      split: {
        continueLabel: "שלם כרטיס 1",
        backLabel: "חזרה לסיכום",
      },
    },
    bookingSummary: review.bookingSummary,
    /** unused local — keep payableLabel for sticky parity */
    _payableLabel: payableLabel,
  };
}
